import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { MGAAttachmentService } from '~/api/services/mgaAttachments'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { RouteTypes } from '~/routes/constants'
import {
  DateInput
} from 'semantic-ui-calendar-react';
import { FormFields } from '~/components/FormFields'
import { useAuth } from '~/routes/components/AuthProvider'
import { Form } from 'semantic-ui-react'
import { getMonths, handleErrors } from '~/utils/helpers'

const CreateMgaAttachment: React.FC = () => {
  const { t } = useTranslation()
  const { systemData: { amountUnit, amountVolume, amountPrice, languages, logged: { languageId } } } = useAuth()
  const priceLabel = `(${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit})`
  const [isBusy, setIsBusy] = useState(false)
  const navigate = useNavigate()
  const { id } = useParams()
  const { handleSubmit, control, reset, watch, setValue } = useForm({
    mode: 'onSubmit'
  })
  const languageLocal = languages.find(language => language.id === languageId)

  const watchInvoiceDate = watch('invoiceDate')

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function renderForm() {
    const months = getMonths()
    return <>
      <Form.Group widths={'equal'} style={{ alignItems: 'baseline' }}>
        <FormFields.Select
          control={control}
          name={'month'}
          options={months.map((month, index) => ({ value: index, text: month }))}
          selection
          selectOnBlur={false}
          placeholder={t('Select month')}
          rules={{
            required: {
              value: true,
              message: t('Please select month')
            }
          }}
          label={t('Month')}
        />
        <FormFields.Select
          control={control}
          name={'year'}
          options={['2022', '2023'].map(year => ({ value: year, text: year }))}
          selection
          selectOnBlur={false}
          rules={{
            required: {
              value: true,
              message: t('Please select year')
            }
          }}
          placeholder={t('Select year')}
          label={t('Year')}
        />
        <FormFields.External
          control={control}
          name={'invoiceDate'}
          label={t('Invoice date')}
          onChange={(event, { name, value }) => setValue('invoiceDate', value)}
          keyName={watchInvoiceDate}
          rules={{
            required: {
              value: true,
              message: t('Select invoice date')
            }
          }}
        >
          <DateInput
            name='dateTime'
            placeholder={t('Select invoice date')}
            closable
            markColor={'blue'}
            localization={languageLocal.code}
            closeOnMouseLeave={false}
            dateFormat={'DD.MM.YYYY'}
            className={'datePicker'}
            value={watchInvoiceDate}
            onKeyPress={handleKeyPress}
            iconPosition='left'
            onChange={(event, { name, value }) => setValue('invoiceDate', value)}
          />
        </FormFields.External>
      </Form.Group>
      <Form.Group widths={'equal'} style={{ alignItems: 'baseline' }}>
        <FormFields.Input
          control={control}
          name={'invoiceNumber'}
          label={t('Bill number')}
          placeholder={t('Enter bill number')}
          rules={{
            required: {
              value: true,
              message: t('Enter bill number')
            },
            maxLength: {
              value: 150,
              message: t('Max. number of characters is 150')
            }
          }}
        />
        <FormFields.Input
          control={control}
          name={'deliveryQuantity'}
          typeInput={'controled'}
          label={t('Delivery quantity (in {{amountUnit}})', { amountUnit })}
          placeholder={t('Enter delivery quantity')}
          type={'number'}
          rules={{
            required: {
              value: true,
              message: t('Enter delivery quantity')
            },
            min: {
              value: 0,
              message: t('Please enter positive number')
            }
          }}
        />
        <FormFields.Input
          control={control}
          name={'basePrice'}
          label={t('Base price 4,0% FE/3,4% EW {{priceLabel}}', { priceLabel })}
          placeholder={t('Enter base price')}
          typeInput={'controled'}
          allowDecimals
          rules={{
            required: {
              value: true,
              message: t('Enter base price')
            },
            min: {
              value: 0,
              message: t('Please enter positive number')
            }
          }}
        />
      </Form.Group>
      <Form.Group widths={'equal'} style={{ alignItems: 'baseline' }}>
        <FormFields.Input
          control={control}
          name={'deductionOfConventionalMilk'}
          label={t('Deduction of conventional milk {{priceLabel}}', { priceLabel })}
          placeholder={t('Enter deduction')}
          typeInput={'controled'}
          allowDecimals
          rules={{
            required: {
              value: true,
              message: t('Enter deduction')
            }
          }}
        />
        <FormFields.Input
          control={control}
          name={'fatCorrection'}
          label={t('Fat correction {{priceLabel}}', { priceLabel })}
          placeholder={t('Enter fat correction')}
          typeInput={'controled'}
          allowDecimals
          rules={{
            required: {
              value: true,
              message: t('Enter fat correction')
            }
          }}
        />
        <FormFields.Input
          control={control}
          name={'proteinCorrection'}
          label={t('Protein correction {{priceLabel}}', { priceLabel })}
          placeholder={t('Enter protein correction')}
          typeInput={'controled'}
          allowDecimals
          rules={{
            required: {
              value: true,
              message: t('Enter protein correction')
            }
          }}
        />
      </Form.Group>
      <Form.Group widths={'equal'} style={{ alignItems: 'baseline' }}>
        <FormFields.Input
          control={control}
          name={'qualityDiscount'}
          label={t('Quality discount {{priceLabel}}', { priceLabel })}
          placeholder={t('Enter quality discount')}
          typeInput={'controled'}
          allowDecimals
          rules={{
            required: {
              value: true,
              message: t('Enter quality discount')
            }
          }}
        />
        <FormFields.Input
          control={control}
          name={'quantityBonus'}
          label={t('Quantity bonus {{priceLabel}}', { priceLabel })}
          placeholder={t('Enter quantity bonus')}
          typeInput={'controled'}
          allowDecimals
          rules={{
            required: {
              value: true,
              message: t('Enter quantity bonus')
            }
          }}
        />
        <FormFields.Input
          control={control}
          name={'grossAmountMilkBill'}
          label={t('Gross amount according to milk bill ({{amountPrice}})', { amountPrice: amountPrice == 'e' ? '€' : '€ Ct' })}
          placeholder={t('Enter gross amount')}
          typeInput={'controled'}
          allowDecimals
          rules={{
            required: {
              value: true,
              message: t('Enter gross amount')
            },
            min: {
              value: 0,
              message: t('Please enter positive number')
            }
          }}
        />
      </Form.Group>
    </>
  }

  function convertDate(date) {
    const [day, month, yearAndTime] = date.split('.')
    const year = yearAndTime?.split(' ')[0]
    return (`${year}-${month}-${day}`)
  }

  async function onSubmit(data) {
    setIsBusy(true)
    try {
      const nonNumberKeys = ['invoiceDate', 'month', 'invoiceNumber']
      data.invoiceDate = convertDate(data.invoiceDate)
      const monthParsed = `${data.year}-${data.month + 1 < 10 ? `0${data.month + 1}` : data.month + 1}-01`
      data.month = monthParsed
      delete data.year
      for (const key in data) {
        if (!nonNumberKeys.find(nonNumberKey => nonNumberKey === key)) {
          data[key] = data[key].toString().replace(',', '.')
        }
      }
      await MGAAttachmentService.storeMgaAttachment(id, data)
      notify(t('MGA attachment has been successfully created'), 'success')
      navigate(RouteTypes.DAIRY_FACTORY_FARMERS_MGA_ATTACHMENTS.replace(':id', id))
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  return <Layout
    title={t('Create MGA attachment')}
    segmented
    isBusy={isBusy}
    close={RouteTypes.DAIRY_FACTORY_FARMERS_MGA_ATTACHMENTS.replace(':id', id)}
    action={t('Create')}
    onSubmit={handleSubmit(onSubmit)}
  >
    {renderForm()}
  </Layout>
}
export default CreateMgaAttachment